import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { examsAPI, coursesAPI } from "../services/api";
import "../styles/AddExamQuestions.css"; // Reuse unified styles
import {
  FaPlus, FaTrash, FaCheck, FaListUl,
  FaSave, FaArrowLeft, FaArrowRight, FaClock, FaInfoCircle, FaAlignLeft
} from "react-icons/fa";
import { toast } from "react-toastify";

const EditExam = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [courses, setCourses] = useState([]);

  // Exam Settings
  const [examData, setExamData] = useState({
    courseId: "",
    title: "",
    description: "",
    duration: 60,
    isMandatory: false,
    allowRetake: false,
    maxAttempts: 1,
    showResultsImmediately: true
  });

  const [questions, setQuestions] = useState([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  // Mobile View Toggle
  const [mobileView, setMobileView] = useState('list');

  // Fetch Data on Mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const coursesRes = await coursesAPI.getAll();
        setCourses(coursesRes.data);

        const examRes = await examsAPI.getById(examId);
        const { exam, questions: fetchedQuestions } = examRes.data;

        setExamData({
          courseId: exam.course_id,
          title: exam.title,
          description: exam.description,
          duration: exam.duration_minutes,
          isMandatory: !!exam.is_mandatory,
          allowRetake: !!exam.allow_retake,
          maxAttempts: exam.max_attempts || 1,
          showResultsImmediately: exam.show_results_immediately !== 0
        });

        // Map fetched questions
        const formattedQuestions = fetchedQuestions.map(q => ({
          id: q.id, // Keep original DB ID
          text: q.question_text,
          type: q.question_type,
          points: q.points,
          options: q.options ? q.options.map(opt => ({
            id: opt.id, // Keep option ID if available
            text: opt.option_text,
            isCorrect: !!opt.is_correct,
            matchText: opt.match_text,
            orderIndex: opt.order_index
          })) : []
        }));

        setQuestions(formattedQuestions);
        if (formattedQuestions.length > 0) {
          setSelectedQuestionId(formattedQuestions[0].id);
        }
      } catch (error) {
        console.error("❌ Error fetching exam data:", error);
        toast.error("❌ تعذر تحميل بيانات الامتحان");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [examId]);

  const handleDetailChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExamData({
      ...examData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleNextStep = () => {
    // Basic validation
    if (!examData.title) return toast.warn("يرجى إدخال عنوان الامتحان");
    setStep(2);
  };

  // --- Question Management ---

  const handleQuestionSelect = (id) => {
    setSelectedQuestionId(id);
    setMobileView('editor');
  };

  const addQuestion = (type) => {
    const baseQuestion = {
      id: Date.now(), // Temporary ID for new questions
      text: "",
      type: type,
      points: 1,
      options: []
    };

    if (type === 'mcq') {
      baseQuestion.options = [
        { text: "", isCorrect: false },
        { text: "", isCorrect: false }
      ];
    } else if (type === 'true_false') {
      baseQuestion.options = [
        { text: "صح (True)", isCorrect: true },
        { text: "خطأ (False)", isCorrect: false }
      ];
    } else if (type === 'matching') {
      baseQuestion.options = [{ text: "", matchText: "" }];
    } else if (type === 'ordering') {
      baseQuestion.options = [{ text: "", orderIndex: 1 }];
    }

    const newQuestions = [...questions, baseQuestion];
    setQuestions(newQuestions);
    setSelectedQuestionId(baseQuestion.id);
    setMobileView('editor');
  };

  const updateQuestion = (id, field, value) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, [field]: value } : q));
  };

  const removeQuestion = async (id) => {
    if (window.confirm("هل أنت متأكد من حذف هذا السؤال؟")) {
      // If it's an existing question (ID < 13 digits is likely DB ID, Date.now is 13 digits)
      // Robust check: if ID is number and small, it's from DB usually
      // However, safely, we can try to delete from API if it seems persistent
      const isNew = String(id).length >= 13;

      if (!isNew) {
        try {
          await examsAPI.deleteQuestion(id);
          toast.success("تم حذف السؤال من قاعدة البيانات");
        } catch (e) {
          console.error(e);
          toast.error("فشل حذف السؤال من السيرفر");
          return;
        }
      }

      const newQuestions = questions.filter(q => q.id !== id);
      setQuestions(newQuestions);
      if (selectedQuestionId === id) {
        setSelectedQuestionId(newQuestions.length > 0 ? newQuestions[0].id : null);
      }
    }
  };

  // Option Updaters
  const updateOption = (qId, idx, field, value) => {
    setQuestions(questions.map(q => {
      if (q.id === qId) {
        const newOptions = [...q.options];
        newOptions[idx] = { ...newOptions[idx], [field]: value };
        return { ...q, options: newOptions };
      }
      return q;
    }));
  };

  const setCorrectOption = (qId, idx) => {
    setQuestions(questions.map(q => {
      if (q.id === qId) {
        const newOptions = q.options.map((opt, i) => ({
          ...opt,
          isCorrect: i === idx
        }));
        return { ...q, options: newOptions };
      }
      return q;
    }));
  };

  const addOption = (qId) => {
    setQuestions(questions.map(q => {
      if (q.id === qId) {
        let newOpt = { text: "", isCorrect: false };
        if (q.type === 'matching') newOpt = { text: "", matchText: "" };
        if (q.type === 'ordering') newOpt = { text: "", orderIndex: q.options.length + 1 };
        return { ...q, options: [...q.options, newOpt] };
      }
      return q;
    }));
  };

  const handleUpdate = async () => {
    try {
      // 1. Update Exam Info
      await examsAPI.update(examId, examData);

      // 2. Add New Questions
      // Filter questions that generate Date.now() IDs (long IDs)
      const newQuestions = questions.filter(q => String(q.id).length >= 13);
      if (newQuestions.length > 0) {
        const formattedNew = newQuestions.map(q => ({
          text: q.text || "سؤال جديد",
          type: q.type,
          points: q.points,
          options: q.options
        }));
        await examsAPI.addQuestions(examId, formattedNew);
      }

      // 3. For existing questions, ideally we update them. 
      // Current API might not support bulk update of questions, so we rely on backend handling or ignore updates to existing text
      // (As per previous logic). If user edits existing question text, valid ID serves as ref if backend supports it.

      toast.success("✅ تم تحديث الامتحان بنجاح!");
      navigate("/exams");
    } catch (error) {
      console.error("❌ Error updating exam:", error);
      toast.error("❌ حدث خطأ أثناء التحديث");
    }
  };

  const selectedQuestion = questions.find(q => q.id === selectedQuestionId);

  if (loading) return <div className="loading-spinner">⏳ جارٍ التحميل...</div>;

  return (
    <div className="add-exam-container fade-in">
      <div className="wizard-header">
        <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <div className="step-num">{step > 1 ? '✓' : '1'}</div>
          <span>بيانات الامتحان</span>
        </div>
        <div className={`step ${step === 2 ? 'active' : ''}`}>
          <div className="step-num">2</div>
          <span>تعديل الأسئلة</span>
        </div>
      </div>

      <div className="wizard-content">
        {step === 1 && (
          <div className="exam-details-card fade-in">
            <h2 style={{ marginBottom: '20px' }}>📝 تعديل بيانات الامتحان</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>الدورة التدريبية</label>
                <select name="courseId" value={examData.courseId} onChange={handleDetailChange} className="form-control" disabled>
                  <option value="">{courses.find(c => c.id === examData.courseId)?.title || "loading..."}</option>
                </select>
                <small style={{ color: 'var(--text-secondary)' }}>لا يمكن تغيير الدورة أثناء التعديل</small>
              </div>

              <div className="form-group">
                <label>عنوان الامتحان</label>
                <input type="text" name="title" className="form-control" value={examData.title} onChange={handleDetailChange} />
              </div>

              <div className="form-group">
                <label><FaClock /> المدة (دقيقة)</label>
                <input type="number" name="duration" className="form-control" value={examData.duration} onChange={handleDetailChange} />
              </div>

              <div className="form-group full-width">
                <label>وصف الامتحان</label>
                <textarea name="description" className="form-control" rows="3" value={examData.description} onChange={handleDetailChange} />
              </div>
            </div>

            <div style={{ marginTop: '20px', textAlign: 'left' }}>
              <button className="btn-finish" onClick={handleNextStep}>التالي: الأسئلة <FaArrowLeft /></button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={`exam-builder-layout fade-in ${mobileView === 'list' ? 'mobile-show-list' : 'mobile-show-editor'}`}>

            {/* Mobile Toggle */}
            <div className="mobile-view-toggle">
              <button
                className={`toggle-btn ${mobileView === 'list' ? 'active' : ''}`}
                onClick={() => setMobileView('list')}
              >
                📌 القائمة
              </button>
              <button
                className={`toggle-btn ${mobileView === 'editor' ? 'active' : ''}`}
                onClick={() => setMobileView('editor')}
                disabled={!selectedQuestionId}
              >
                ✏️ التعديل
              </button>
            </div>

            {/* Sidebar List */}
            <div className="questions-sidebar">
              <div className="sidebar-header">
                <h3>📌 الأسئلة ({questions.length})</h3>
              </div>

              <div className="questions-list">
                {questions.map((q, idx) => (
                  <div
                    key={q.id}
                    className={`question-item ${selectedQuestionId === q.id ? 'active' : ''}`}
                    onClick={() => handleQuestionSelect(q.id)}
                  >
                    <div className="q-item-content">
                      <div className="q-item-title">{q.text || `سؤال ${idx + 1}`}</div>
                      <div className="q-item-meta">
                        <span>{q.type}</span>
                        <span>{q.points} درجة</span>
                      </div>
                    </div>
                    {/* Delete logic */}
                    <button className="btn-icon-danger" onClick={(e) => { e.stopPropagation(); removeQuestion(q.id); }}>
                      <FaTrash size={12} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="sidebar-footer">
                <button onClick={() => addQuestion('mcq')} className="add-q-btn"><FaListUl /> اختيارات</button>
                <button onClick={() => addQuestion('true_false')} className="add-q-btn"><FaCheck /> صواب/خطأ</button>
                <button onClick={() => addQuestion('fill_blank')} className="add-q-btn"><FaAlignLeft /> أكمل</button>
                <button onClick={() => addQuestion('essay')} className="add-q-btn"><FaAlignLeft /> مقالي</button>
              </div>
            </div>

            {/* Editor Panel */}
            <div className="question-editor-panel">
              {selectedQuestion ? (
                <>
                  <div className="editor-header">
                    <button className="back-to-list-btn" onClick={() => setMobileView('list')}>
                      <FaArrowRight /> قائمة الأسئلة
                    </button>
                    <h3>تعديل السؤال</h3>
                    <div className="points-wrapper">
                      <span>الدرجة:</span>
                      <input
                        type="number"
                        className="form-control"
                        style={{ width: '70px', padding: '5px' }}
                        value={selectedQuestion.points}
                        onChange={(e) => updateQuestion(selectedQuestion.id, 'points', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>نص السؤال</label>
                    <textarea
                      className="question-text-area"
                      value={selectedQuestion.text}
                      onChange={(e) => updateQuestion(selectedQuestion.id, 'text', e.target.value)}
                      placeholder="اكتب نص السؤال هنا..."
                    />
                  </div>

                  {/* Options Area */}
                  <div className="options-container">
                    <h4>الخيارات / الإجابات</h4>

                    {selectedQuestion.type === 'mcq' && (
                      <div>
                        {selectedQuestion.options.map((opt, idx) => (
                          <div key={idx} className={`option-row ${opt.isCorrect ? 'correct' : ''}`}>
                            <input type="radio" checked={opt.isCorrect} onChange={() => setCorrectOption(selectedQuestion.id, idx)} />
                            <input
                              type="text"
                              className="option-input"
                              value={opt.text}
                              onChange={(e) => updateOption(selectedQuestion.id, idx, 'text', e.target.value)}
                              placeholder={`الخيار ${idx + 1}`}
                            />
                          </div>
                        ))}
                        <button className="add-q-btn" onClick={() => addOption(selectedQuestion.id)}>+ إضافة خيار</button>
                      </div>
                    )}

                    {selectedQuestion.type === 'true_false' && (
                      <div>
                        {selectedQuestion.options.map((opt, idx) => (
                          <div key={idx} className={`option-row ${opt.isCorrect ? 'correct' : ''}`}>
                            <input type="radio" checked={opt.isCorrect} onChange={() => setCorrectOption(selectedQuestion.id, idx)} />
                            <span>{opt.text}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {(selectedQuestion.type === 'fill_blank' || selectedQuestion.type === 'essay') && (
                      <div className="alert-box">
                        <FaInfoCircle />
                        <p style={{ margin: 0, fontSize: '0.9rem' }}>
                          {selectedQuestion.type === 'fill_blank' ? "لإضافة فراغ، استخدم القوسين [مثال] في نص السؤال." : "هذا السؤال يصحح يدوياً."}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <FaPlus size={40} style={{ marginBottom: '20px' }} />
                  <h3>اختر سؤالاً للتعديل أو أضف سؤالاً جديداً</h3>
                </div>
              )}
            </div>

            <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
              <button className="btn-prev" onClick={() => setStep(1)}><FaArrowRight /> البيانات الأساسية</button>
              <button className="btn-finish" onClick={handleUpdate}><FaSave /> حفظ التعديلات</button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default EditExam;
